import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Plus, Mail, UserCheck, UserX, Trash2, RefreshCw,
  AlertTriangle, CheckCircle2, Clock, Copy, X, ShieldCheck, Lock, Eye, EyeOff,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useLanguage } from '../contexts/LanguageContext';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';

/**
 * SuperAdminsPage — primary Super Admin's control panel for managing other
 * Super Admins. Backend gates ALL writes on `is_main_user=true` so a regular
 * ultra_admin sees a 403 if they try to load this; we mirror that with a
 * client-side check on `/api/auth/me` to avoid showing an empty shell.
 */
const STATUS_PILL = {
  active:          { trLabel: 'Aktif',          enLabel: 'Active',          cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  pending_invite:  { trLabel: 'Davet Bekliyor', enLabel: 'Pending Invite',  cls: 'bg-amber-100 text-amber-800 border-amber-200',       Icon: Clock        },
  inactive:        { trLabel: 'Pasif',          enLabel: 'Inactive',        cls: 'bg-gray-100 text-gray-600 border-gray-200',          Icon: UserX        },
};

function fmtDate(iso, tr) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(tr ? 'tr-TR' : 'en-US', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

export default function SuperAdminsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [me, setMe]               = useState(null);
  const [admins, setAdmins]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [confirm, setConfirm]     = useState(null); // { kind, target, action: () => Promise<void> }
  const [inviteLink, setInviteLink] = useState(null); // { email, link } shown after invite (in case email failed)

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/super-admins');
      setAdmins(r.data || []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error(tr ? 'Bu sayfaya sadece birincil Süper Admin erişebilir.' : 'Only the primary Super Admin can access this page.');
        navigate('/ultra-admin/dashboard');
      } else {
        toast.error(tr ? 'Liste yüklenemedi.' : 'Failed to load list.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, tr]);

  useEffect(() => {
    apiClient.get('/auth/me').then(r => setMe(r.data)).catch(() => {});
    fetchAdmins();
  }, [fetchAdmins]);

  const isPrimary = me && me.is_super_admin;

  const handleInvite = async (a) => {
    try {
      const r = await apiClient.post(`/ultra-admin/super-admins/${a.id}/invite`);
      if (r.data.sent) {
        toast.success(tr ? `Davet e-postası ${a.email} adresine gönderildi.` : `Invitation sent to ${a.email}.`);
      } else {
        // Email failed — show link so the primary can copy it manually.
        setInviteLink({ email: a.email, link: r.data.setup_link });
        toast.warning(tr ? 'E-posta gönderilemedi — bağlantıyı manuel paylaşabilirsiniz.' : 'Email failed — share the link manually.');
      }
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Davet gönderilemedi.' : 'Failed to send invitation.'));
    }
  };

  const handleDeactivate = async (a) => {
    try {
      await apiClient.patch(`/ultra-admin/super-admins/${a.id}/deactivate`);
      toast.success(tr ? 'Pasife alındı.' : 'Deactivated.');
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleActivate = async (a) => {
    try {
      await apiClient.patch(`/ultra-admin/super-admins/${a.id}/activate`);
      toast.success(tr ? 'Aktife alındı.' : 'Activated.');
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (a, ultraPassword) => {
    try {
      await apiClient.delete(`/ultra-admin/super-admins/${a.id}`, {
        params: { ultra_password: ultraPassword },
      });
      toast.success(tr ? 'Silindi.' : 'Deleted.');
      fetchAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
      throw e; // re-throw so the modal keeps the password input open on failure
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        {/* Top nav */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6 flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Shield className="h-4 w-4 text-violet-600" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">{tr ? 'Süper Admin Yönetimi' : 'Super Admin Management'}</h1>
                <p className="text-xs text-gray-400">{tr ? 'Sadece birincil Süper Admin' : 'Primary Super Admin only'}</p>
              </div>
            </div>
            <LanguageToggle variant="admin" />
          </div>
        </nav>

        <div className="flex-1 p-6 max-w-5xl mx-auto w-full" data-testid="super-admins-page">
          <PageIntro {...getPageIntro('super-admins', language)} />
          {/* Header card */}
          <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-2xl p-6 mb-6 shadow-lg">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <ShieldCheck className="h-7 w-7" />
                  {tr ? 'Süper Adminler' : 'Super Admins'}
                </h2>
                <p className="text-violet-100 text-sm mt-1 max-w-md">
                  {tr
                    ? 'Süper Adminler tüm sahiplik kurallarını override edebilir. Her override audit log\'a kaydedilir.'
                    : 'Super Admins can override all ownership rules. Every override is audit-logged.'}
                </p>
              </div>
              <Button
                data-testid="add-super-admin-btn"
                onClick={() => setShowAdd(true)}
                disabled={!isPrimary}
                className="bg-white text-violet-700 hover:bg-violet-50 font-semibold rounded-xl gap-2">
                <Plus className="h-4 w-4" />
                {tr ? 'Yeni Süper Admin' : 'New Super Admin'}
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <Shield className="h-4 w-4 text-gray-400" />
                {tr ? `Toplam ${admins.length} kayıt` : `${admins.length} record(s)`}
              </div>
              <button onClick={fetchAdmins} disabled={loading}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {admins.length === 0 ? (
              <div className="text-center py-16 text-gray-400 text-sm">
                {loading ? (tr ? 'Yükleniyor…' : 'Loading…') : (tr ? 'Henüz Süper Admin yok.' : 'No Super Admins yet.')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {admins.map(a => {
                  const pill = STATUS_PILL[a.status] || STATUS_PILL.inactive;
                  const PillIcon = pill.Icon;
                  const canInvite     = !a.password_set && a.status !== 'inactive';
                  const canDeactivate = !a.is_main_user && a.status === 'active';
                  const canActivate   = !a.is_main_user && a.status === 'inactive';
                  const canDelete     = !a.is_main_user && !a.last_login;
                  return (
                    <li key={a.id} className="px-5 py-4 flex items-start gap-4 hover:bg-gray-50/50 transition-colors" data-testid={`super-admin-row-${a.id}`}>
                      <div className="w-11 h-11 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <Shield className="h-5 w-5 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 truncate">{a.full_name || '—'}</span>
                          {a.is_main_user && (
                            <span className="bg-violet-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                              {tr ? 'Birincil' : 'Primary'}
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${pill.cls}`}>
                            <PillIcon className="h-3 w-3" />
                            {tr ? pill.trLabel : pill.enLabel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5 truncate">{a.email}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mt-2">
                          <span>{tr ? 'Son giriş' : 'Last login'}: <strong className="text-gray-600">{fmtDate(a.last_login, tr)}</strong></span>
                          <span>{tr ? 'Davet' : 'Invited'}: <strong className="text-gray-600">{fmtDate(a.invited_at, tr)}</strong></span>
                          {a.invited_by && <span>{tr ? 'Davet eden' : 'Invited by'}: <strong className="text-gray-600">{a.invited_by}</strong></span>}
                        </div>
                      </div>
                      {/* Actions */}
                      {!a.is_main_user && (
                        <div className="flex flex-wrap gap-2 flex-shrink-0">
                          {canInvite && (
                            <button
                              data-testid={`invite-btn-${a.id}`}
                              onClick={() => handleInvite(a)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-violet-100 hover:bg-violet-200 text-violet-700 text-xs font-semibold">
                              <Mail className="h-3.5 w-3.5" />
                              {a.invited_at ? (tr ? 'Yeniden Davet' : 'Resend') : (tr ? 'Davet Gönder' : 'Send Invite')}
                            </button>
                          )}
                          {canDeactivate && (
                            <button
                              data-testid={`deactivate-btn-${a.id}`}
                              onClick={() => setConfirm({ kind: 'deactivate', target: a, action: () => handleDeactivate(a) })}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold">
                              <UserX className="h-3.5 w-3.5" />
                              {tr ? 'Pasife Al' : 'Deactivate'}
                            </button>
                          )}
                          {canActivate && (
                            <button
                              data-testid={`activate-btn-${a.id}`}
                              onClick={() => handleActivate(a)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-semibold">
                              <UserCheck className="h-3.5 w-3.5" />
                              {tr ? 'Aktife Al' : 'Activate'}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              data-testid={`delete-btn-${a.id}`}
                              onClick={() => setConfirm({ kind: 'delete', target: a, action: (pwd) => handleDelete(a, pwd) })}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold">
                              <Trash2 className="h-3.5 w-3.5" />
                              {tr ? 'Sil' : 'Delete'}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Add Super Admin modal ───────────────────────────── */}
      {showAdd && (
        <AddSuperAdminModal
          tr={tr}
          onClose={() => setShowAdd(false)}
          onCreated={(newAdmin, sendInviteNow) => {
            setShowAdd(false);
            fetchAdmins();
            if (sendInviteNow) {
              handleInvite(newAdmin);
            }
          }}
        />
      )}

      {/* ── Confirm modal (deactivate/delete) ───────────────── */}
      {confirm && (
        <ConfirmModal
          tr={tr}
          confirm={confirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {/* ── Manual invite link fallback (when email send fails) ── */}
      {inviteLink && (
        <div className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <Mail className="h-5 w-5 text-violet-600" />
                {tr ? 'Davet Bağlantısı' : 'Invitation Link'}
              </h3>
              <button onClick={() => setInviteLink(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {tr ? `Aşağıdaki bağlantıyı ${inviteLink.email} adresine manuel olarak gönderin (24 saat geçerli):` : `Send the link below manually to ${inviteLink.email} (valid 24 hours):`}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4">
              <code className="text-xs text-gray-700 break-all font-mono">{inviteLink.link}</code>
            </div>
            <Button
              onClick={() => { navigator.clipboard.writeText(inviteLink.link); toast.success(tr ? 'Kopyalandı' : 'Copied'); }}
              className="w-full gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl">
              <Copy className="h-4 w-4" />
              {tr ? 'Bağlantıyı Kopyala' : 'Copy link'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Add Super Admin Modal ────────────────────────────────────────────────────
function AddSuperAdminModal({ tr, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [sendNow, setSendNow] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;
    setBusy(true);
    try {
      const r = await apiClient.post('/ultra-admin/super-admins', {
        email: email.trim().toLowerCase(),
        full_name: name.trim(),
      });
      toast.success(tr ? 'Süper Admin oluşturuldu.' : 'Super Admin created.');
      onCreated(r.data, sendNow);
    } catch (err) {
      toast.error(err.response?.data?.detail || (tr ? 'Hata oluştu.' : 'Error.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4" data-testid="add-super-admin-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
            <Plus className="h-5 w-5 text-violet-600" />
            {tr ? 'Yeni Süper Admin' : 'New Super Admin'}
          </h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <Label className="text-xs">{tr ? 'Ad Soyad' : 'Full Name'}</Label>
            <Input
              data-testid="new-admin-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={tr ? 'Ad Soyad' : 'Full Name'}
              required
              className="rounded-xl mt-1" />
          </div>
          <div>
            <Label className="text-xs">{tr ? 'E-posta' : 'Email'}</Label>
            <Input
              data-testid="new-admin-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
              className="rounded-xl mt-1" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 select-none">
            <input
              type="checkbox"
              data-testid="send-invite-now-cb"
              checked={sendNow}
              onChange={e => setSendNow(e.target.checked)}
              className="rounded" />
            {tr ? 'Oluşturduktan sonra hemen davet e-postası gönder' : 'Send invitation email immediately after creation'}
          </label>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            {tr ? 'Vazgeç' : 'Cancel'}
          </button>
          <Button
            type="submit"
            data-testid="create-super-admin-btn"
            disabled={busy}
            className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl">
            {busy ? (tr ? 'Oluşturuluyor…' : 'Creating…') : (tr ? 'Oluştur' : 'Create')}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * ConfirmModal — extracted so it can hold its own password state without
 * remounting on every parent render. For "delete" actions it gates the
 * destructive call behind an Ultra Password (mirrors User Management).
 */
function ConfirmModal({ tr, confirm, onClose }) {
  const [pwd, setPwd]       = useState('');
  const [show, setShow]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const isDelete            = confirm.kind === 'delete';

  const submit = async () => {
    if (busy) return;
    if (isDelete && !pwd.trim()) return;
    setBusy(true);
    try {
      await confirm.action(pwd.trim() || undefined);
      onClose();
    } catch {
      // handler already toasts; keep modal open on failure (esp. wrong password)
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4" data-testid="super-admin-confirm-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isDelete ? 'bg-red-100' : 'bg-amber-100'}`}>
            <AlertTriangle className={`h-6 w-6 ${isDelete ? 'text-red-600' : 'text-amber-600'}`} />
          </div>
          <h3 className="font-bold text-gray-800 text-lg">
            {isDelete
              ? (tr ? 'Süper Admini sil' : 'Delete Super Admin')
              : (tr ? 'Süper Admini pasife al' : 'Deactivate Super Admin')}
          </h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          <strong className="text-gray-900">{confirm.target.full_name || confirm.target.email}</strong>{' '}
          {isDelete
            ? (tr ? 'kalıcı olarak silinecek. Bu işlem geri alınamaz.' : 'will be permanently deleted. This cannot be undone.')
            : (tr ? 'pasife alınacak ve giriş yapamayacak. Daha sonra geri alabilirsiniz.' : 'will be deactivated and unable to login. You can re-activate later.')}
        </p>

        {isDelete && (
          <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700 mb-2">
              <Lock className="h-3.5 w-3.5" />
              {tr ? 'Ultra Password' : 'Ultra Password'}
            </div>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && pwd.trim() && !busy) submit(); }}
                placeholder={tr ? 'Ultra şifresini girin' : 'Enter Ultra Password'}
                autoFocus
                data-testid="super-admin-delete-ultra-password"
                className="w-full px-3 py-2 pr-9 rounded-lg border border-red-200 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none bg-white"
              />
              <button type="button" onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-red-600 mt-1.5">
              {tr ? 'Bu işlem geri alınamaz. Devam etmek için Ultra Password gereklidir.'
                  : 'This action cannot be undone. Ultra Password is required to continue.'}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            {tr ? 'Vazgeç' : 'Cancel'}
          </button>
          <button
            data-testid="confirm-action-btn"
            onClick={submit}
            disabled={busy || (isDelete && !pwd.trim())}
            className={`flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${isDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
            {busy
              ? (tr ? 'İşleniyor…' : 'Working…')
              : (isDelete ? (tr ? 'Evet, Sil' : 'Yes, Delete') : (tr ? 'Evet, Pasife Al' : 'Yes, Deactivate'))}
          </button>
        </div>
      </div>
    </div>
  );
}

